import React, {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useLocation} from 'react-router'
import {matchPath} from 'react-router-dom'
import {toggle} from 'opticks'

import {useDeviceLayout} from '@daedalus/atlas/context/deviceLayout'
import {Page} from '@daedalus/core/src/analytics/types/Events'
import {
  getIsAuthenticated,
  getUser
} from '@daedalus/core/src/auth/modules/selectors'
import {getBrand} from '@daedalus/core/src/brand/modules/selectors'
import {checkIsBrandVio} from '@daedalus/core/src/brand/utils'
import {getPersistedConfiguration} from '@daedalus/core/src/offer/services/offerConfigurationApi/store'
import {ImageProvider as ImageProviderType} from '@daedalus/core/src/staticContent/types/ImageProvider'
import {getUrlParams} from '@daedalus/core/src/utils/url'
import {ProfileKey} from '@findhotel/sapi'

import {FreezeBottomSheet} from './components/FreezeBottomSheet'
import {FreezeConfirmationOverlay} from './components/FreezeConfirmationOverlay'
import {
  defaultExternalActions,
  ExternalActions,
  ExternalActionsProvider
} from './context/ExternalActionsContext'
import {ImageProvider} from './context/ImageProviderContext'
import {SapiProvider} from './context/SapiContext'
import {useAnalyticsPage} from './hooks/useAnalyticsPage'
import {useGetUserJwtToken} from './hooks/useGetUserJwtToken'
import {useSapiClient} from './hooks/useSapiClient'
import {
  getApiContext,
  getApplication,
  getIsDealFreezeEnabled
} from './modules/selector'
import {
  ApplicationNames,
  setApiContext,
  setApplication,
  setDealFreezeEnabled
} from './modules/slice'
import {
  useCheckUserLimitQuery,
  useGetFrozenBookingsQuery
} from './services/dealFreezeApi'
import {isCheckInDateValid} from './utils/OfferEligiblity'

const SUPPORTED_LANGUAGES = ['en', 'en-GB']

interface OverlayStateType {
  type: string
  extraState?: Record<string, unknown>
}

interface LocationState {
  overlay?: OverlayStateType[]
}

interface Props {
  imageProvider: ImageProviderType
  appCurrency: string | undefined
  sapiProfileKey: ProfileKey
  application: ApplicationNames
  appVersion: string
  boVioEndpoint: string
  boVioProfile: string
  checkoutUrl: string
  bookingManagementUrl: string
  languageCode: string
  actions: ExternalActions
  getSearchId: (state: any) => null | string
  getCountryCode: (state: any) => string | null
}

const InitializeApiCalls = () => {
  const isAuthenticated = useSelector(getIsAuthenticated)
  const {page} = useSelector(getApplication)
  const {boVioEndpoint, userToken} = useSelector(getApiContext)
  // on payment page we don't need to make these two api calls
  const isPaymentPage = page === Page.Payment
  const shouldSkip =
    !userToken || !boVioEndpoint || isPaymentPage || !isAuthenticated

  useCheckUserLimitQuery({}, {skip: shouldSkip})
  useGetFrozenBookingsQuery({}, {skip: shouldSkip})
  return null
}

export const DealFreeze = ({
  imageProvider,
  appCurrency,
  sapiProfileKey,
  application,
  appVersion,
  boVioEndpoint,
  boVioProfile,
  checkoutUrl,
  bookingManagementUrl,
  languageCode,
  actions,
  getSearchId,
  getCountryCode
}: Props) => {
  const dispatch = useDispatch()
  const urlParams = getUrlParams()
  const location = useLocation()
  const {pathname} = location
  const locationOverlayState = location.state as LocationState
  const isAccommodationPageMobile = locationOverlayState?.overlay?.some(
    (overlay: OverlayStateType) => overlay.type === 'hotelDetails'
  )
  const isAccommodationPageDesktop = matchPath(location.pathname, {
    path: '/Hotel/:hotelSlug(\\d+)'
  })
  const isAccommodationPage =
    isAccommodationPageMobile || Boolean(isAccommodationPageDesktop)

  const brand = useSelector(getBrand)
  const {checkIn} = urlParams
  const {isMobile} = useDeviceLayout()

  const [currency, setCurrency] = useState(appCurrency)

  useEffect(() => {
    if (currency) return
    const {currency: urlCurrency} = getUrlParams()
    setCurrency(urlCurrency as string)
  }, [currency, location.search])

  const sapiClient = useSapiClient({
    sapiProfileKey,
    currency
  })

  const isAuthenticated = useSelector(getIsAuthenticated)
  const user = useSelector(getUser)
  const isDealFreezeEnabled = useSelector(getIsDealFreezeEnabled)
  const userAttributes = user?.attributes
  const {given_name, family_name, email} = userAttributes || {}
  const hasUserRequiredData = Boolean(given_name && family_name && email)

  /**
   * We only show the deal freeze when a user has all the required data (first name, last name, email)
   * Unauthenticated users can still see the deal freeze, but when they click on the button, SUSI overlay opens.
   */
  const shouldUserSeeDealFreeze = !isAuthenticated || hasUserRequiredData

  const userToken = useGetUserJwtToken()
  const configuration = getPersistedConfiguration()
  const dealFreezeConfiguration = configuration.dealFreeze
  const {
    offerCheckInDayDistanceEndLimit,
    offerCheckInDayDistanceStartLimit,
    enabled: isDealFreezeEnabledConfiguration
  } = dealFreezeConfiguration || {}
  const isCheckInValid =
    Boolean(checkIn) &&
    isCheckInDateValid(
      checkIn as string,
      new Date(),
      offerCheckInDayDistanceStartLimit,
      offerCheckInDayDistanceEndLimit
    )
  const isVioBrand = checkIsBrandVio(brand)
  const countryCode = useSelector(getCountryCode)
  const searchId = useSelector(getSearchId)
  const page = useAnalyticsPage(application, pathname, isAccommodationPage)
  const externalActions = {
    ...defaultExternalActions,
    ...actions
  }

  useEffect(() => {
    const clientUserAgent = `${application}/${appVersion}`
    dispatch(
      setApiContext({boVioProfile, userToken, clientUserAgent, boVioEndpoint})
    )
  }, [
    boVioProfile,
    userToken,
    appVersion,
    application,
    dispatch,
    boVioEndpoint
  ])

  useEffect(() => {
    dispatch(
      setApplication({
        name: application,
        version: appVersion,
        countryCode,
        searchId,
        page,
        currency,
        languageCode,
        checkoutUrl,
        bookingManagementUrl
      })
    )
  }, [
    dispatch,
    searchId,
    countryCode,
    application,
    appVersion,
    currency,
    page,
    languageCode,
    checkoutUrl,
    bookingManagementUrl
  ])

  useEffect(() => {
    const isDealFreezeAbEnabled = Boolean(
      isMobile &&
        isVioBrand &&
        currency &&
        isDealFreezeEnabledConfiguration &&
        SUPPORTED_LANGUAGES.includes(languageCode) &&
        isCheckInValid &&
        shouldUserSeeDealFreeze &&
        toggle('c3f23fa5-dealfreeze-public-access', false, true)
    )

    dispatch(setDealFreezeEnabled(isDealFreezeAbEnabled))
  }, [
    isMobile,
    isVioBrand,
    currency,
    isDealFreezeEnabledConfiguration,
    isCheckInValid,
    shouldUserSeeDealFreeze,
    dispatch,
    languageCode
  ])

  /**
   * There is no need for this DealFreeze component on Payment page
   */
  if (page === Page.Payment) return null
  if (!isDealFreezeEnabled) return null

  return (
    <ExternalActionsProvider value={externalActions}>
      <ImageProvider value={imageProvider}>
        <SapiProvider value={sapiClient}>
          <InitializeApiCalls />
          <FreezeBottomSheet />
          <FreezeConfirmationOverlay />
        </SapiProvider>
      </ImageProvider>
    </ExternalActionsProvider>
  )
}
